import { ScrollText } from "lucide-react";
import { useAuditEvents } from "@/api/queries";
import type { AuditEvent, AuditFilters } from "@/api/types";
import { StatusBadge } from "@/components/feedback/status-badge";
import { OrgLink, StoreLink } from "@/components/navigation/entity-links";
import { AdminTable, type Column } from "@/components/tables/admin-table";
import { type ResourceTableProps, tablePaging } from "@/components/tables/types";
import { formatDateTime } from "@/lib/dates";
import { shortId } from "@/lib/formatters";
import { AUDIT_RESULT } from "@/lib/status";
import { ActorLabel, ResourceRefLink } from "./audit-parts";

export function AuditTable({ query, onChange, toolbar, hide = [], onRowClick, filtered }: ResourceTableProps<AuditFilters>) {
  const q = useAuditEvents(query);
  const columns: Column<AuditEvent>[] = [
    { id: "at", header: "Timestamp", sortKey: "at", alwaysVisible: true, cell: (e) => <span className="font-mono text-[11.5px]">{formatDateTime(e.at)}</span> },
    { id: "actor", header: "Actor", cell: (e) => <ActorLabel actor={e.actor} /> },
    { id: "action", header: "Action", cell: (e) => <span className="font-mono text-[11.5px]">{e.action}</span> },
    { id: "result", header: "Result", cell: (e) => <StatusBadge config={AUDIT_RESULT[e.result]} /> },
    { id: "resource", header: "Resource", cell: (e) => <ResourceRefLink resource={e.resource} /> },
    {
      id: "resourceId",
      header: "Resource ID",
      defaultHidden: true,
      cell: (e) => (
        <span className="font-mono text-[11px]" title={e.resource.id ?? ""}>
          {shortId(e.resource.id)}
        </span>
      ),
    },
    { id: "organization", header: "Organization", cell: (e) => <OrgLink org={e.organization} /> },
    { id: "store", header: "Store", cell: (e) => <StoreLink store={e.store} /> },
  ];
  return (
    <AdminTable
      id="audit"
      label="Audit events"
      resourceName="audit events"
      columns={columns.filter((c) => !hide.includes(c.id))}
      rows={q.data?.items}
      rowKey={(e) => e.id}
      isPending={q.isPending}
      isFetching={q.isFetching}
      error={q.error}
      onRetry={() => void q.refetch()}
      {...tablePaging(query, q.data?.total, onChange)}
      onRowClick={onRowClick ? (e) => onRowClick(e.id) : undefined}
      toolbar={toolbar}
      empty={
        filtered
          ? { icon: ScrollText, title: "No audit events match", description: "Adjust the date range or filters." }
          : { icon: ScrollText, title: "No audit events recorded", description: "Changes by admins, users, Rogue and the system appear here." }
      }
    />
  );
}
